import axios from 'axios';
import { lerToken, lerRefreshToken, limparSessao, salvarSessao } from 'services/sessao';
import { plataforma } from 'services/plataforma';

/**
 * Evento que anuncia "a sessao acabou de expirar".
 *
 * O interceptor nao alcanca o `navigate` do React Router -- ele vive fora da arvore de componentes,
 * e nao ha hook para chamar dali. Antes o desvio era `window.location.href = '/login'`, que resolve
 * o problema pelo caminho mais caro possivel: navegacao de documento inteiro, recarregando o bundle
 * e jogando fora todo o estado do React. Num app nativo e pior, porque o servidor estatico do
 * Capacitor nao tem fallback de history por padrao e o destino pode simplesmente nao existir.
 *
 * Entao aqui so se anuncia; quem navega e um componente dentro do <Router> (ver App.jsx). O padrao
 * ja existia no projeto: o BackendStatusProvider escuta 'backend:down' do mesmo jeito.
 */
export const EVENTO_SESSAO_EXPIRADA = 'sessao:expirada';

// O prefixo da API vive AQUI, e não em cada chamada. Até esta versão o axios.create() vinha vazio e
// os 51 pontos que falam com o backend montavam a URL na mão --
// `${process.env.REACT_APP_API_BASE_URL}/usuario/${id}` -- espalhados por 25 arquivos. Duas
// consequências: apontar o front para outra origem (o app nativo precisa de URL absoluta, porque
// numa WebView "/api" resolve para o bundle local) exigiria editar os 51, e um build sem a variável
// no ambiente produzia silenciosamente URLs como "undefined/usuario/1" -- não havia fallback em
// lugar nenhum.
//
// O fallback '/api' não é adivinhação: é o valor que está no .env versionado desde sempre, e o
// mesmo que o nginx e o setupProxy.js esperam. Serve para o build que esquecer a variável falhar
// como o site normal, e não como uma tela em branco.
//
// As chamadas ao IBGE e ao ViaCEP continuam em `axios` cru, de propósito -- ver a nota do
// interceptor de request abaixo.
//
// O header X-Plataforma ('web' | 'android' | 'ios') diz ao backend de onde vem a sessão, e é o que
// alimenta o registro de acesso por plataforma (backend: AcessoUsuarioService, gravado no login e no
// /login/refresh). Vai nos DOIS clients porque o de renovação não passa por interceptor nenhum, e é
// justamente a renovação que marca o dia de uso no app. Ver prompts/registro-de-acesso-por-plataforma.md.
const HEADERS_DA_API = { 'X-Plataforma': plataforma() };

const apiClient = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL || '/api',
    headers: HEADERS_DA_API,
});

// Todo request por este client leva o Bearer. E por isso que IBGE e ViaCEP NAO passam por aqui:
// mandar o JWT do usuario para um terceiro seria vazar a credencial, e um 403 vindo de la cairia no
// interceptor de resposta abaixo e deslogaria alguem por causa de uma consulta de CEP. Aquelas seis
// chamadas usam `axios` cru de propósito -- ver pages/Usuario/Novo.jsx e services/municipios.js.
apiClient.interceptors.request.use((config) => {
    const token = lerToken();
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Endpoints publicos (espelham USUARIO_PUBLICOS_POST/GET do SecurityConfig): o visitante nunca teve
// token, entao 401/403 ali e credencial invalida ou falha da propria chamada. Nem desloga, nem renova.
//
// A comparacao e por `includes` sobre error.config.url (ver o interceptor abaixo), e o que esse
// campo contem MUDOU quando a baseURL subiu para o axios.create: antes era "/api/login", agora e
// "/login" -- o axios guarda baseURL e url separados, e so os concatena na hora de despachar.
// A lista continua casando nos dois formatos porque toda entrada comeca com "/", que e o comeco do
// caminho nos dois casos; nenhuma delas dependia do prefixo "/api". Isto esta coberto por teste
// (api.test.js), e nao por deducao: e o tipo de coisa que falha em producao deslogando usuario
// legitimo no meio do cadastro, sem erro nenhum no console.
const ENDPOINTS_PUBLICOS = [
    '/login',
    '/usuario/add',
    '/usuario/checkcpf',
    '/usuario/checktelefone',
    '/usuario/checkemail',
    '/usuario/cpfhub',
    '/usuario/esqueciminhasenha',
    '/usuario/validarcodigo',
    '/usuario/atualizarsenha',
    '/usuario/validartoken',
    '/contato',
    // Rascunho do cadastro (CADASTRO_PUBLICOS_POST/_GET): quem preenche o wizard ainda nao tem
    // conta, e um 403 aqui expulsaria para o /login no meio do cadastro -- perdendo exatamente o
    // preenchimento que este endpoint existe para nao perder.
    '/cadastro/rascunho',
    // A versao exigida do app (SecurityConfig.APP_PUBLICOS_GET): perguntada ao abrir, antes do login.
    '/app/versao',
];

// Endpoints em que 401/403 NAO desloga ninguem: os publicos, mais o /admin.
//
// No /admin o 403 e falta de permissao, com a sessao perfeitamente valida -- deslogar ali seria
// expulsar do site um usuario comum que so digitou uma URL. Mas o 401 continua sendo token vencido,
// e por isso o /admin fica FORA da lista de cima: ate a v1.30 ele estava nela, a renovacao nunca
// acontecia numa chamada administrativa, e quinze minutos depois do ultimo refresh toda tela do admin
// mostrava "nao foi possivel carregar" (ADM-106/401) ate alguma chamada fora do /admin -- a NavBar, o
// sino -- renovar o token por acaso. Ver prompts/admin-sem-renovacao-de-sessao.md.
const ENDPOINTS_SEM_REDIRECT = [...ENDPOINTS_PUBLICOS, '/admin'];

/**
 * Cliente SEM interceptor nenhum, usado so para chamar /login/refresh.
 *
 * Existe para que a recursao seja impossivel por CONSTRUCAO, e nao por acordo: se a renovacao
 * passasse pelo apiClient, um 401 dela cairia neste mesmo interceptor, que tentaria renovar de novo.
 * Hoje o /login/refresh casa com '/login' em ENDPOINTS_PUBLICOS e isso ja o protegeria -- mas seria uma
 * protecao que depende de ninguem mexer naquela lista, e o custo de nao depender e uma linha.
 */
const clienteDeRenovacao = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL || '/api',
    headers: HEADERS_DA_API,
});

// Trava de uma expiracao so. Um dashboard que dispara cinco chamadas em paralelo toma cinco 401
// quando o token vence, e antes disso cada uma removia o token e reatribuia window.location.href --
// cinco navegacoes empilhadas. Com o evento e pior do que redundante: seriam cinco navigate() e o
// historico ficaria com quatro entradas de /login que o botao voltar teria de percorrer.
//
// Volta a false no primeiro sucesso: se uma chamada respondeu, ha sessao viva de novo (o login
// depois do redirecionamento e o caso normal), e a proxima expiracao precisa ser anunciada.
let expiracaoJaAnunciada = false;

/**
 * A renovacao em curso, ou null. E ESTA VARIAVEL que e a fila.
 *
 * O caso que ela resolve: o dashboard dispara cinco chamadas de uma vez e o JWT vence no meio. Sem
 * ela, as cinco tomam 401 e as cinco chamam /login/refresh -- e como cada renovacao ROTACIONA o
 * token (o usado e revogado), a segunda chegaria apresentando um refresh que a primeira acabou de
 * invalidar. O backend leria isso como REUSO e revogaria a familia inteira, deslogando de verdade
 * quem so tinha uma tela carregando.
 *
 * Guardando a Promise, a primeira chamada renova e as outras quatro aguardam a MESMA promessa. Uma
 * ida ao servidor, um token novo, quatro requisicoes reexecutadas com ele.
 */
let renovacaoEmAndamento = null;

/** Uma renovacao por vez. Quem chegar durante uma em curso recebe a promessa dela. */
function renovarSessao() {
    if (renovacaoEmAndamento) return renovacaoEmAndamento;

    renovacaoEmAndamento = clienteDeRenovacao
        .post('/login/refresh', { refreshToken: lerRefreshToken() })
        .then(({ data }) => {
            salvarSessao(data);
            return data.token;
        })
        .finally(() => {
            // Limpo no finally, e nao no then: se a renovacao falhar e a variavel ficasse com a
            // promessa rejeitada, toda chamada seguinte herdaria a MESMA falha para sempre -- a
            // sessao nunca mais se recuperaria, nem depois de um login novo.
            renovacaoEmAndamento = null;
        });

    return renovacaoEmAndamento;
}

function encerrarSessao() {
    if (expiracaoJaAnunciada) return;
    expiracaoJaAnunciada = true;
    limparSessao();
    window.dispatchEvent(new CustomEvent(EVENTO_SESSAO_EXPIRADA));
}

apiClient.interceptors.response.use(
    response => {
        expiracaoJaAnunciada = false;
        return response;
    },
    async error => {
        const status = error.response?.status;
        const isUnauthorized = status === 401 || status === 403;
        const url = error.config?.url;
        const isPublicEndpointRequest = ENDPOINTS_PUBLICOS.some((endpoint) => url?.includes(endpoint));
        const naoDesloga = ENDPOINTS_SEM_REDIRECT.some((endpoint) => url?.includes(endpoint));

        // ── Renovacao ────────────────────────────────────────────────────────────────────────
        // So em 401, NUNCA em 403. Os dois codigos dizem coisas diferentes: 401 e "esta credencial
        // nao vale mais", que e o que um JWT vencido produz e o que a renovacao conserta; 403 e
        // "voce nao tem permissao", que e o caso de um usuario comum esbarrando em /admin. Renovar
        // ali trocaria um token perfeitamente valido por outro para tentar de novo a mesma coisa
        // que vai ser recusada igual -- e, pior, gastaria uma rotacao a cada tentativa.
        //
        // `_jaTentouRenovar` impede a segunda tentativa sobre a MESMA requisicao: se ela tomar 401
        // outra vez ja com o token novo, o problema nao e o token, e insistir seria um laco.
        const podeRenovar = status === 401
            && !isPublicEndpointRequest
            && !error.config?._jaTentouRenovar
            && Boolean(lerRefreshToken());

        if (podeRenovar) {
            error.config._jaTentouRenovar = true;
            try {
                const novoToken = await renovarSessao();
                error.config.headers.Authorization = `Bearer ${novoToken}`;
                return apiClient(error.config);
            } catch {
                // A renovacao foi recusada: refresh vencido, revogado, ou familia derrubada por
                // deteccao de reuso. Nao ha o que tentar -- so a senha resolve.
                encerrarSessao();
                return Promise.reject(error);
            }
        }

        // Aviso global de backend fora do ar, desativado. Dispara o evento que o
        // BackendStatusProvider escuta para trocar a tela toda pela pagina de manutencao.
        // Para reativar, descomentar as tres linhas do if E as duas declaracoes junto dele.
        //
        // As declaracoes estao comentadas junto, e nao soltas acima, porque nada mais as le: como
        // variavel sem uso e warning de lint, e o CRA trata warning como ERRO quando CI=true, as
        // duas sozinhas derrubavam o build no servidor -- falha que nao aparece rodando
        // `npm run build` na maquina de quem desenvolve.
 //       const isNetworkDown = !error.response;
 //       const isServerError = error.response?.status >= 500;
 //       if (isNetworkDown || isServerError) {
 //           window.dispatchEvent(new CustomEvent('backend:down'));
 //       }

        // ── Fim de sessao ────────────────────────────────────────────────────────────────────
        // Chega aqui quem nao pode renovar: 403, endpoint publico, requisicao que ja tentou uma
        // vez, ou -- o caso que mantem o comportamento antigo vivo -- quem NAO TEM refresh token.
        // O /admin entra no `naoDesloga` mesmo assim (ver ENDPOINTS_SEM_REDIRECT).
        //
        // Esse ultimo e o que permite publicar este frontend sem derrubar ninguem: quem entrou
        // antes da v1.11.3 tem so o JWT guardado, e para essa pessoa tudo funciona exatamente como
        // funcionava -- 401 desloga e manda para o /login. Ela ganha o refresh no proximo login.
        //
        // 401/403 num endpoint publico (login incluido) significa credenciais invalidas ou falha da
        // propria chamada, nao sessao expirada -- nao desloga nem redireciona um visitante anonimo.
        if (isUnauthorized && !naoDesloga) {
            encerrarSessao();
        }

        return Promise.reject(error);
    }
);

export default apiClient;
